import { IconButton } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import {
  useDynamicFormsUtils,
  usePageContext,
  useTableFiltersActions,
} from "@ryde/redux";
import {
  availableToAddFiltersControlsSF,
  IDropdownItem,
  sortArrayAlphabetically,
  useIsMobile,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import {
  FilterDropdownWrapper,
  FilterSelectControl,
  selectStyles,
  selectStylesMobile,
} from "./parts";

export const FilterAddButton: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const availableToAddCustomFields = useRecoilValue(
    availableToAddFiltersControlsSF(entity),
  );

  const { getGridControlLabelById } = useDynamicFormsUtils();

  const options = useMemo(() => {
    return sortArrayAlphabetically(
      availableToAddCustomFields.map(({ id }) => ({
        value: id,
        label: getGridControlLabelById(id || -1),
      })),
      "label",
    ) as IDropdownItem[];
  }, [availableToAddCustomFields, getGridControlLabelById]);

  const { handleAddFilter } = useTableFiltersActions();
  const [isOpen, setIsOpen] = useState(false);

  const addFilterHandler = useEvent((e: unknown) => {
    handleAddFilter(
      (e as IDropdownItem[] | undefined)?.[0]?.value as number | undefined,
    );
    setIsOpen(false);
  });

  const handleToggle = useEvent(() => setIsOpen(prev => !prev));
  const handleClose = useEvent(() => setIsOpen(false));

  const stylesComputed = useMemo(() => {
    return isMobile ? selectStylesMobile : selectStyles;
  }, [isMobile]);

  return (
    <FilterDropdownWrapper
      isFilterItem={false}
      isOpen={isOpen}
      onClose={handleClose}
      target={
        <FramerTooltip content={t("general.ADD_NEW_FILTER")}>
          <IconButton data-e2e="svg-add-filter" onClick={handleToggle}>
            <PIC.PlusSvg />
          </IconButton>
        </FramerTooltip>
      }
    >
      <Select
        isMulti
        // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
        autoFocus
        backspaceRemovesValue={false}
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Control: FilterSelectControl,
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={true}
        menuIsOpen
        options={options}
        onChange={addFilterHandler}
        placeholder={`${t(`general.SEARCH`)}...`}
        styles={stylesComputed}
        tabSelectsValue={false}
        noOptionsMessage={() => t(`errors.noOptionsMessage`)}
      />
    </FilterDropdownWrapper>
  );
};
